/**
 * Diagnostic overlay shown when ?aura-debug=1 is present in the URL.
 *
 * Purpose: when production behaviour diverges from local Playwright tests,
 * we need a way to see what the user's browser is actually reporting
 * without asking them to set up remote-debugging or paste console output.
 * This overlay surfaces every signal that drives the panel-layout decision
 * — matchMedia results, navigator hints, viewport metrics, the rendered
 * panel rect — in a high-contrast fixed-position box.  Activation is
 * gated by a query string so it doesn't pollute real users.
 */

/**
 * Probe the host page for any element with CSS `zoom` set — the exact
 * detection the reading-mask uses to scale its strip on hosts like
 * Beacon.  Returns the offending element + computed zoom so we can see
 * whether mask scaling is reading the right value.
 */
function probeZoomedElement(): { selector: string; zoom: number } | null {
  const wrappers = Array.from(
    document.querySelectorAll(
      'main, article, [role="main"], main > *, article > *, body > div, body > main > div',
    ),
  );
  for (const el of wrappers) {
    if (!(el instanceof HTMLElement)) continue;
    const z = parseFloat(window.getComputedStyle(el).zoom);
    if (Number.isFinite(z) && z > 0 && z !== 1) {
      const cls = (el.className && typeof el.className === 'string')
        ? '.' + el.className.split(/\s+/).filter(Boolean).slice(0, 2).join('.')
        : '';
      return { selector: el.tagName.toLowerCase() + cls, zoom: z };
    }
  }
  return null;
}

/**
 * Estimate the host page's effective text line height in device pixels —
 * same logic the reading-mask uses to size its strip on zoomed hosts.
 */
function probeEffectiveLineHeight(): { sample: string; lineHeight: number } | null {
  const samples = [
    'main p', 'article p', '[role="main"] p',
    'main', 'article', 'body',
  ];
  for (const sel of samples) {
    const el = document.querySelector(sel) as HTMLElement | null;
    if (!el) continue;
    const cs = window.getComputedStyle(el);
    let lh = parseFloat(cs.lineHeight);
    if (!Number.isFinite(lh) || lh <= 0) {
      const fs = parseFloat(cs.fontSize);
      if (Number.isFinite(fs) && fs > 0) lh = fs * 1.5;
    }
    if (!Number.isFinite(lh) || lh <= 0) continue;
    let zoom = 1;
    let cur: HTMLElement | null = el;
    while (cur && cur !== document.documentElement) {
      const z = parseFloat(window.getComputedStyle(cur).zoom);
      if (Number.isFinite(z) && z > 0 && z !== 1) zoom *= z;
      cur = cur.parentElement as HTMLElement | null;
    }
    return { sample: sel, lineHeight: lh * zoom };
  }
  return null;
}

function readMaskState(): {
  exists: boolean;
  display: string;
  parent: string;
  topH: string;
  botH: string;
  topRect: string;
  botRect: string;
  botComputedTop: string;
  botComputedBottom: string;
  botComputedPosition: string;
  topComputedDisplay: string;
  botComputedDisplay: string;
  botZIndex: string;
  handleTop: string;
  handleDisplay: string;
} {
  const mask = document.querySelector('[data-aura="reading-mask"]') as HTMLElement | null;
  const top = document.querySelector('[data-aura="reading-mask"] .aura-mask-top') as HTMLElement | null;
  const bot = document.querySelector('[data-aura="reading-mask"] .aura-mask-bottom') as HTMLElement | null;
  const handle = document.querySelector('[data-aura="reading-mask-handle"]') as HTMLElement | null;
  const fmtRect = (el: HTMLElement | null) => {
    if (!el) return '<n/a>';
    const r = el.getBoundingClientRect();
    return `${Math.round(r.left)},${Math.round(r.top)} ${Math.round(r.width)}x${Math.round(r.height)}`;
  };
  const botCs = bot ? getComputedStyle(bot) : null;
  return {
    exists: !!mask,
    display: mask?.style.display ?? '',
    parent: mask?.parentElement?.tagName?.toLowerCase() ?? '<no-parent>',
    topH: top?.style.height ?? '',
    botH: bot?.style.height ?? '',
    topRect: fmtRect(top),
    botRect: fmtRect(bot),
    botComputedTop: botCs?.top ?? '',
    botComputedBottom: botCs?.bottom ?? '',
    botComputedPosition: botCs?.position ?? '',
    topComputedDisplay: top ? getComputedStyle(top).display : '',
    botComputedDisplay: bot ? getComputedStyle(bot).display : '',
    botZIndex: bot ? getComputedStyle(bot).zIndex : '',
    handleTop: handle?.style.top ?? '',
    handleDisplay: handle?.style.display ?? '',
  };
}

/**
 * Summarise the containing-block-creating CSS properties on an element.
 *
 * `position: fixed` descendants anchor to the viewport UNLESS some ancestor
 * has one of these properties applied with a non-initial value, in which
 * case THAT ancestor becomes the containing block.  Beacon's <body> has
 * one of these set and that is why our mask's `bottom: 0` is anchoring
 * to body's bottom (taller than viewport) instead of the viewport bottom.
 * This probe reports each property's computed value so we can identify the
 * culprit at a glance.
 */
function probeContainingBlockTriggers(el: Element | null): string {
  if (!el) return '<no element>';
  const cs = getComputedStyle(el);
  const props = [
    'transform',
    'filter',
    'perspective',
    'contain',
    'will-change',
    'backdrop-filter',
    'isolation',
  ] as const;
  const interesting = props
    .map((p) => {
      const v = cs.getPropertyValue(p).trim();
      const isDefault =
        v === '' ||
        v === 'none' ||
        v === 'auto' ||
        v === 'normal' ||
        (p === 'isolation' && v === 'auto');
      return isDefault ? null : `${p}=${v}`;
    })
    .filter((s): s is string => s !== null);
  return interesting.length === 0 ? 'none' : interesting.join(', ');
}

function readSignals(panel: HTMLElement | null): string {
  const mm = (q: string): string =>
    typeof window.matchMedia === 'function'
      ? String(window.matchMedia(q).matches)
      : 'n/a';
  const uaData = (navigator as unknown as {
    userAgentData?: { mobile?: boolean; platform?: string };
  }).userAgentData;
  const rect = panel?.getBoundingClientRect();
  const zoom = probeZoomedElement();
  const lineHeight = probeEffectiveLineHeight();
  const mask = readMaskState();
  const vv = (window as Window & { visualViewport?: VisualViewport }).visualViewport;
  const computedPanelWidth = panel ? getComputedStyle(panel).width : null;
  const shell = document.querySelector('[data-aura="shell"]') as HTMLElement | null;
  const shellTransform = shell ? getComputedStyle(shell).transform : null;
  const bodyTransform = document.body
    ? getComputedStyle(document.body).transform
    : null;
  const htmlTransform = document.documentElement
    ? getComputedStyle(document.documentElement).transform
    : null;

  const lines = [
    'AURA debug',
    '───────────────────────',
    `innerWidth:  ${window.innerWidth}`,
    `innerHeight: ${window.innerHeight}`,
    `screen.w/h:  ${screen.width}x${screen.height}`,
    `DPR:         ${window.devicePixelRatio}`,
    '',
    '— matchMedia —',
    `(hover: hover)        → ${mm('(hover: hover)')}`,
    `(hover: none)         → ${mm('(hover: none)')}`,
    `(pointer: fine)       → ${mm('(pointer: fine)')}`,
    `(pointer: coarse)     → ${mm('(pointer: coarse)')}`,
    `(any-pointer: coarse) → ${mm('(any-pointer: coarse)')}`,
    `(any-pointer: fine)   → ${mm('(any-pointer: fine)')}`,
    `(min-width: 600px)    → ${mm('(min-width: 600px)')}`,
    '',
    '— navigator —',
    `maxTouchPoints:      ${navigator.maxTouchPoints}`,
    `ontouchstart in win: ${'ontouchstart' in window}`,
    `uaData.mobile:       ${uaData?.mobile ?? 'n/a'}`,
    `uaData.platform:     ${uaData?.platform ?? 'n/a'}`,
    '',
    '— visualViewport —',
    vv
      ? `width:  ${Math.round(vv.width)} (layout: ${window.innerWidth})`
      : 'visualViewport: <unavailable>',
    vv ? `height: ${Math.round(vv.height)}` : '',
    vv ? `scale:  ${vv.scale}` : '',
    vv
      ? `offset: ${Math.round(vv.offsetLeft)},${Math.round(vv.offsetTop)}`
      : '',
    vv && Math.abs(vv.width - window.innerWidth) > 1
      ? '→ visual ≠ layout (page is wide-laid-out)'
      : '',
    '',
    '— ancestor transforms —',
    `html.transform: ${htmlTransform || 'none'}`,
    `body.transform: ${bodyTransform || 'none'}`,
    `shell.transform: ${shellTransform || 'none'}`,
    '',
    '— containing-block triggers —',
    `html: ${probeContainingBlockTriggers(document.documentElement)}`,
    `body: ${probeContainingBlockTriggers(document.body)}`,
    `html.scrollHeight: ${document.documentElement?.scrollHeight ?? 'n/a'}`,
    `body.scrollHeight: ${document.body?.scrollHeight ?? 'n/a'}`,
    '',
    '— panel —',
    rect
      ? `rect: ${Math.round(rect.left)},${Math.round(rect.top)} ${Math.round(rect.width)}x${Math.round(rect.height)}`
      : 'panel rect: <not found>',
    `computed width: ${computedPanelWidth ?? 'n/a'}`,
    rect && vv && rect.width >= vv.width - 1
      ? '→ FULL-WIDTH (visible-viewport)'
      : rect && rect.width >= window.innerWidth - 1
      ? '→ FULL-WIDTH (layout-viewport)'
      : rect
      ? '→ SIDE-RAIL'
      : '',
    '',
    '— host content zoom probe —',
    zoom
      ? `found: ${zoom.selector}  zoom=${zoom.zoom}`
      : 'no zoom detected (scale = 1)',
    lineHeight
      ? `effective line-height: ${lineHeight.lineHeight.toFixed(1)}px (sample: ${lineHeight.sample})`
      : 'effective line-height: <unknown>',
    '',
    '— reading mask —',
    mask.exists
      ? `display: ${mask.display || '(default)'}  parent: <${mask.parent}>`
      : 'mask: <NOT ACTIVE — enable ADHD profile>',
    mask.exists ? `top: h=${mask.topH}  rect=${mask.topRect}  disp=${mask.topComputedDisplay}` : '',
    mask.exists ? `bot: h=${mask.botH}  rect=${mask.botRect}  disp=${mask.botComputedDisplay}  z=${mask.botZIndex}` : '',
    mask.exists ? `bot.computed: pos=${mask.botComputedPosition}  top=${mask.botComputedTop}  bottom=${mask.botComputedBottom}` : '',
    mask.exists ? `handle: top=${mask.handleTop}  display=${mask.handleDisplay || '(default)'}` : '',
  ].filter((s) => s !== '' || true); // keep empty separators
  return lines.join('\n');
}

/**
 * Mount the diagnostic overlay if the host page URL includes ?aura-debug=1.
 *
 * The overlay is appended to document.body (NOT the shadow root) so it
 * sits above everything visually and is straightforward to inspect.  It
 * reads `panel` (which lives in closed shadow DOM) by capturing a
 * reference from the caller — `getBoundingClientRect()` works on closed
 * shadow elements as long as you hold the reference.
 */
export function maybeMountDebugOverlay(panel: HTMLElement | null): () => void {
  let isDebug = false;
  try {
    isDebug = new URLSearchParams(window.location.search).get('aura-debug') === '1';
  } catch {
    return () => {};
  }
  if (!isDebug) return () => {};

  const overlay = document.createElement('div');
  overlay.setAttribute('data-aura', 'debug-overlay');
  overlay.style.cssText = [
    'position: fixed',
    'top: 8px',
    'left: 8px',
    'z-index: 2147483647',
    'background: rgba(0, 0, 0, 0.92)',
    'color: #00ff66',
    "font: 11px/1.4 ui-monospace, Menlo, Consolas, 'Courier New', monospace",
    'padding: 10px 12px',
    'border-radius: 6px',
    'max-width: 360px',
    'white-space: pre',
    'pointer-events: none',
    'box-shadow: 0 4px 16px rgba(0,0,0,0.4)',
    'user-select: text',
  ].join('; ');

  const update = () => {
    overlay.textContent = readSignals(panel);
  };

  const target = document.body ?? document.documentElement;
  if (!target) return () => {};
  target.appendChild(overlay);
  update();

  // Recompute on viewport changes so the user can rotate / resize and see
  // signals update live.
  const onResize = () => update();
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);
  // Re-read after a short delay so the panel rect settles after the
  // open/close transition (300 ms in our CSS).
  setTimeout(update, 500);

  // Poll every 750 ms so mask state appears as soon as the user toggles
  // ADHD on/off, without needing to refresh.
  const pollId = window.setInterval(update, 750);
  const onClickAnything = () => setTimeout(update, 400);
  document.addEventListener('click', onClickAnything, true);

  return () => {
    window.removeEventListener('resize', onResize);
    window.removeEventListener('orientationchange', onResize);
    document.removeEventListener('click', onClickAnything, true);
    window.clearInterval(pollId);
    overlay.remove();
  };
}
